import React, { PureComponent } from 'react';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Container,
  InputGroup,
  Row
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import cx from 'classnames';
import URI from 'urijs';
import { connect } from 'react-redux';
import MaterialIcon from 'material-icons-react';

// ducks
import {
  paginateThis,
  LOAD_FAVORITES,
  getCountsByPropertyClass
} from '../../ducks/FavoriteSearch';
import { PROPERTY_TYPE_MAP } from '../../ducks/PropertySearch';

// middleware
import { cleanQuery } from '../../middleware/client';

// containers
import FavoriteSearchResults from './FavoriteSearchResults';
import FavoriteSearchEmailModal from './FavoriteSearchEmailModal';
import FavoriteSearchContactAgentModal from './FavoriteSearchContactAgentModal';

// components
import { ucFirst } from '../../utilities';
import Paginator from '../../components/paginator/paginator';

// partials
import PropertyClassDropdown from '../../partials/property/property-class-dropdown';

// modules
import BS from '../../theme/Bootstrap';
import styles from '../Containers.module.scss';

const BREADCRUMB_TITLES = {
  ...PROPERTY_TYPE_MAP
};

class FavoriteSearch extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showEmailModal: false,
      showContactModal: false
    };
    this.handleEmailSubmit = this.handleEmailSubmit.bind(this);
    this.handleContactSubmit = this.handleContactSubmit.bind(this);
  }

  render() {
    const {
      query,
      match: { path },
      view,
      countsByPropertyClass
    } = this.props;
    const { showEmailModal, showContactModal } = this.state;
    const pathname = path;
    const propertyClass = query.propertyClass || 'residential';
    const linkQuery = cleanQuery({
      propertyClass: propertyClass !== 'residential' ? propertyClass : undefined,
      view: view
    });

    return (
      <Container fluid className={styles.PropertySearch}>
        <Container>
          <Row>
            <Breadcrumb>
              <LinkContainer exact to="/">
                <BreadcrumbItem>
                  <Link to="/">Home</Link>
                </BreadcrumbItem>
              </LinkContainer>
              <LinkContainer exact to="/favorites">
                <BreadcrumbItem>
                  <Link to="/favorites">Favorites</Link>
                </BreadcrumbItem>
              </LinkContainer>
              <BreadcrumbItem active>{BREADCRUMB_TITLES[propertyClass]}</BreadcrumbItem>
            </Breadcrumb>
          </Row>
        </Container>
        <Row
          className={cx(
            styles.searchHeader,
            BS.pt3,
            styles['searchHeader' + ucFirst(propertyClass)]
          )}
        >
          <Container>
            <div className={cx(BS.dFlex, BS.justifyContentBetween)}>
              <div className={BS.dFlex}>
                <PropertyClassDropdown
                  propertyClass={propertyClass}
                  basePath={pathname}
                  propertyTypeMap={PROPERTY_TYPE_MAP}
                  BS={BS}
                  className={BS.wAuto}
                  query={{
                    view
                  }}
                  counts={countsByPropertyClass}
                />
                <InputGroup className={cx(BS.wAuto, BS.inputGroupGutter, BS.pb3)}>
                  <Button
                    color="dark-blue"
                    outline
                    className={BS.dPrintNone}
                    onClick={() => window.print && window.print()}
                  >
                    Print Listings
                  </Button>
                  <Button
                    color="dark-blue"
                    outline
                    className={BS.dPrintNone}
                    onClick={() => this.setState({ showEmailModal: true })}
                  >
                    Email Listings
                  </Button>
                  <Button
                    color="dark-blue"
                    outline
                    className={BS.dPrintNone}
                    onClick={() => this.setState({ showContactModal: true })}
                  >
                    Contact Agents
                  </Button>
                </InputGroup>
              </div>
              <div>
                <InputGroup className={cx(BS.wAuto, BS.inputGroupGutter)}>
                  <Button
                    tag={Link}
                    to={URI(pathname)
                      .search({
                        ...linkQuery,
                        view: 'compare'
                      })
                      .toString()}
                    color={cx({
                      secondary: view === 'compare',
                      'dark-blue': view !== 'compare'
                    })}
                    className={cx(BS.mb3)}
                  >
                    Compare
                  </Button>
                  <Button
                    tag={Link}
                    to={URI(pathname)
                      .search({
                        ...linkQuery,
                        view: undefined
                      })
                      .toString()}
                    color={cx({
                      secondary: view === 'thumb',
                      'dark-blue': view !== 'thumb'
                    })}
                    className={cx(BS.mb3)}
                  >
                    Thumb
                  </Button>
                  <Button
                    tag={Link}
                    to={URI(pathname)
                      .search({
                        ...linkQuery,
                        view: 'map'
                      })
                      .toString()}
                    color={cx({
                      secondary: view === 'map',
                      'dark-blue': view !== 'map'
                    })}
                    className={cx(BS.mb3)}
                  >
                    Map
                  </Button>
                  <span className={cx(BS.ml2, BS.mr1)}>|</span>
                  <Button
                    color="link"
                    className={cx(styles.favoritesLink)}
                    title="Share Listing"
                    onClick={() => this.setState({ showEmailModal: true })}
                  >
                    <MaterialIcon icon={'share'} />
                  </Button>
                </InputGroup>
              </div>
            </div>
          </Container>
        </Row>
        <Row className={cx(styles.searchPagination, BS.pt3)}>
          <Container>
            <Paginator listId="favorites" to={pathname} query={linkQuery} top hidePage />
          </Container>
        </Row>
        <FavoriteSearchResults
          listId="favorites"
          view={view}
          propertyClass={propertyClass}
          shouldUpdate={(props, nextProps) => {
            return props.view !== nextProps.view;
          }}
        />
        <Row className={cx(styles.searchPagination, BS.mt3)}>
          <Container>
            <Paginator
              listId="favorites"
              to={pathname}
              query={linkQuery}
              bottom
              hidePage
            />
          </Container>
        </Row>
        <Alert color="warning" className={BS.textCenter}>
          <p>
            All information regarding a property for sale, rental or financing, school
            information and public tax records is from sources deemed reliable; PROVIDED,
            HOWEVER, that representations and warranties are not made about the accuracy
            thereof, and such information is subject to errors, omissions, updates,
            modifications, changes of price, rent rates, commissions, prior sales, leases
            or financing, changes in school boundaries, or withdrawal without notice. All
            information displayed on this Website should be independently verified if any
            person intends to engage in a transaction based upon such information.
          </p>
        </Alert>
        {showEmailModal && (
          <FavoriteSearchEmailModal
            isOpen={showEmailModal}
            propertyClass={propertyClass}
            onToggle={isOpen =>
              this.setState({
                showEmailModal: isOpen !== undefined ? isOpen : !this.state.showEmailModal
              })
            }
          />
        )}
        {showContactModal && (
          <FavoriteSearchContactAgentModal
            backdrop="static"
            isOpen={showContactModal}
            propertyClass={propertyClass}
            onToggle={isOpen =>
              this.setState({
                showContactModal:
                  isOpen !== undefined ? isOpen : !this.state.showContactModal
              })
            }
          />
        )}
      </Container>
    );
  }

  componentDidMount() {
    this.props.loadFavorites();
  }

  componentWillUnmount() {
    this.props.clearResults();
  }

  componentDidUpdate(prevProps) {
    if (this.props.query.page !== prevProps.query.page) {
      window.scrollTo(0, 0);
    }

    if (
      this.props.query.propertyClass !== prevProps.query.propertyClass &&
      prevProps.query.propertyClass
    ) {
      this.props.clearResults();
    }
  }

  handleEmailSubmit(values, dispatch, props) {}

  handleContactSubmit(values, dispatch, props) {}
}

const FavoriteSearchDecorated = paginateThis.decorate(
  connect(
    (state, props) => {
      let search = URI(props.location.search).search(true);
      return {
        view: search.view || 'thumb',
        countsByPropertyClass: getCountsByPropertyClass(state)
      };
    },
    {
      loadFavorites: LOAD_FAVORITES
    }
  )(FavoriteSearch, (state, props) => {
    return {
      propertyClass: props.propertyClass
    };
  })
);

FavoriteSearchDecorated.defaultProps = {
  propertyClass: 'residential'
};

export default FavoriteSearchDecorated;



// WEBPACK FOOTER //
// ./src/containers/FavoriteSearch/FavoriteSearch.js